import React, { useState, useRef } from 'react';
import { Upload, X, Lock, Loader2 } from 'lucide-react';

/** Purpose: Executes { on close, on submit, providers = [] }. Inputs: { onClose, onSubmit, providers }. Output: Operation result. */
export default function NewRecordModal({ onClose, onSubmit, providers = [] }) {
  const [formData, setFormData] = useState({
    title: '',
    date: '',
    provider: '',
    facility: '',
    type: 'Lab Results',
    description: '',
    tags: ''
  });
  const [providerMode, setProviderMode] = useState('new');
  const [selectedProviderId, setSelectedProviderId] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef(null);
  const selectedProvider = providers.find((provider) => provider.id === selectedProviderId) || null;

  /** Purpose: Handles drag. Inputs: e. Output: Operation result. */
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  /** Purpose: Handles drop. Inputs: e. Output: Operation result. */
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      addFiles(Array.from(e.dataTransfer.files));
    }
  };

  /** Purpose: Creates files. Inputs: newFiles. Output: Operation result. */
  const addFiles = (newFiles) => {
    const validFiles = newFiles.filter(file => {
      const allowedTypes = [
        'application/pdf',
        'image/jpeg',
        'image/png',
        'image/gif',
        'image/webp',
        'text/plain',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ];
      return allowedTypes.includes(file.type) && file.size <= 50 * 1024 * 1024;
    });
    setSelectedFiles(prev => [...prev, ...validFiles]);
  };

  /** Purpose: Removes file. Inputs: index. Output: Operation result. */
  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  /** Purpose: Executes format file size. Inputs: bytes. Output: Operation result. */
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  /** Purpose: Handles submit. Inputs: e. Output: Promise with operation result. */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');

    if (providerMode === 'existing' && !selectedProviderId) {
      setSubmitError('Please select an existing provider.');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const payload = {
        ...formData,
        provider: providerMode === 'existing' ? (selectedProvider?.name || '') : formData.provider,
        facility: providerMode === 'existing' ? (selectedProvider?.organization || '') : formData.facility,
        providerMode,
        selectedProviderId: providerMode === 'existing' ? selectedProviderId : '',
        createProvider: providerMode === 'new',
        providerDraft: providerMode === 'new'
          ? {
              name: formData.provider,
              organization: formData.facility
            }
          : null,
        tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean),
        files: selectedFiles
      };

      await onSubmit({
        ...payload
      });
    } catch (err) {
      setSubmitError(err.message || 'Failed to upload record');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-slate-200">
          <h2 className="text-2xl font-bold text-slate-900">Upload Medical Record</h2>
          <p className="text-slate-600 mt-1">Add a new record to your secure vault</p>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {submitError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">
              {submitError}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Record Title *
            </label>
            <input
              type="text"
              required
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., Annual Physical Examination"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Date of Service *
              </label>
              <input
                type="date"
                required
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Record Type *
              </label>
              <select
                required
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option>Lab Results</option>
                <option>Consultation Notes</option>
                <option>Imaging</option>
                <option>Prescriptions</option>
                <option>Immunizations</option>
                <option>Surgical Reports</option>
                <option>Other</option>
              </select>
            </div>
          </div>
          
          <div className="space-y-3">
            <label className="block text-sm font-medium text-slate-700">
              Provider Source *
            </label>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setProviderMode('existing')}
                className={`px-3 py-2 rounded-lg text-sm border ${
                  providerMode === 'existing'
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-slate-700 border-slate-300'
                }`}
              >
                Select Existing
              </button>
              <button
                type="button"
                onClick={() => setProviderMode('new')}
                className={`px-3 py-2 rounded-lg text-sm border ${
                  providerMode === 'new'
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-slate-700 border-slate-300'
                }`}
              >
                Create New
              </button>
            </div>

            {providerMode === 'existing' ? (
              <div className="space-y-2">
                <select
                  value={selectedProviderId}
                  onChange={(e) => setSelectedProviderId(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select provider</option>
                  {providers.map((provider) => (
                    <option key={provider.id} value={provider.id}>
                      {provider.name} - {provider.organization}
                    </option>
                  ))}
                </select>
                {selectedProvider && (
                  <p className="text-xs text-slate-500">
                    Using provider record: {selectedProvider.name} ({selectedProvider.organization})
                  </p>
                )}
                {providers.length === 0 && (
                  <p className="text-xs text-slate-500">
                    No providers available yet. Switch to "Create New".
                  </p>
                )}
              </div>
            ) : (
              <>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Healthcare Provider *
                  </label>
                  <input
                    type="text"
                    required={providerMode === 'new'}
                    value={formData.provider}
                    onChange={(e) => setFormData({ ...formData, provider: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Dr. Sarah Johnson, MD"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Facility/Organization *
                  </label>
                  <input
                    type="text"
                    required={providerMode === 'new'}
                    value={formData.facility}
                    onChange={(e) => setFormData({ ...formData, facility: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., City Medical Center"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    A new provider record will be created when you upload.
                  </p>
                </div>
              </>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Brief description of the record contents"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Tags (comma-separated)
            </label>
            <input
              type="text"
              value={formData.tags}
              onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., routine, preventive, cardiology"
            />
          </div>

          {/* File Upload Section */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Attach Documents
            </label>
            <div
              className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors cursor-pointer ${
                dragActive 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-slate-300 hover:border-slate-400'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={(e) => addFiles(Array.from(e.target.files || []))}
                accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx,.txt"
              />
              <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
              <p className="text-slate-600">
                Drag & drop files here, or <span className="text-blue-600 font-medium">browse</span>
              </p>
              <p className="text-xs text-slate-500 mt-1">
                PDF, images, Word docs up to 50MB • Files are encrypted at rest
              </p>
            </div>

            {/* Selected Files List */}
            {selectedFiles.length > 0 && (
              <div className="mt-3 space-y-2">
                {selectedFiles.map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-lg">
                        {file.type.startsWith('image/') ? '🖼️' : file.type === 'application/pdf' ? '📄' : '📎'}
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{file.name}</p>
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                          <span>{formatFileSize(file.size)}</span>
                          <span className="flex items-center gap-1 text-green-600">
                            <Lock className="w-3 h-3" />
                            Will be encrypted
                          </span>
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeFile(index);
                      }}
                      className="p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 px-6 py-3 border border-slate-300 text-slate-700 rounded-lg font-medium hover:bg-slate-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg font-medium hover:from-blue-700 hover:to-indigo-700 transition-all shadow-md disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Uploading...
                </>
              ) : (
                'Upload Record'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
